import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import api from "services/api";
import { resolveUploadUrl } from "utils";
import { getAssetUrlFromMap, SITE_ASSET_FALLBACKS } from "utils/siteAssets";

function AsetGambarFormDialog({ open, item, onClose, onSaved }) {
  const [form, setForm] = useState({ nama: "", deskripsi: "", halaman: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      nama: item.nama || "",
      deskripsi: item.deskripsi || "",
      halaman: item.halaman || "",
    });
    setFile(null);
    setPreview(
      item.file_url
        ? resolveUploadUrl(item.file_url)
        : getAssetUrlFromMap({}, item.kode, SITE_ASSET_FALLBACKS[item.kode])
    );
  }, [open, item]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    if (selected) setPreview(URL.createObjectURL(selected));
  };

  const handleSubmit = async () => {
    if (!form.nama.trim()) {
      alert("Nama aset wajib diisi");
      return;
    }

    const payload = new FormData();
    payload.append("nama", form.nama.trim());
    payload.append("deskripsi", form.deskripsi.trim());
    payload.append("halaman", form.halaman.trim());
    if (file) payload.append("file", file);

    setSaving(true);
    try {
      await api.put(`aset-gambar/${item.kode}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || "Gagal memperbarui aset gambar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Kembalikan foto ke default bawaan sistem?")) return;
    try {
      await api.delete(`aset-gambar/${item.kode}/file`);
      onSaved();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || "Gagal mengembalikan foto default");
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ubah Aset Gambar — {item.kode}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <MDInput label="Nama *" fullWidth value={form.nama} onChange={handleChange("nama")} />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="Deskripsi"
              fullWidth
              multiline
              rows={2}
              value={form.deskripsi}
              onChange={handleChange("deskripsi")}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput label="Digunakan di halaman" fullWidth value={form.halaman} onChange={handleChange("halaman")} />
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Ganti foto (JPG/PNG/WebP, maks. 5 MB)
            </MDTypography>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
          </Grid>
          {preview ? (
            <Grid item xs={12}>
              <MDBox
                component="img"
                src={preview}
                alt={form.nama}
                borderRadius="lg"
                width="100%"
                maxHeight="240px"
                sx={{ objectFit: "cover" }}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <MDButton variant="text" color="warning" onClick={handleReset}>
          Kembalikan Default
        </MDButton>
        <MDBox>
          <MDButton variant="text" color="secondary" onClick={onClose} sx={{ mr: 1 }}>
            Batal
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan"}
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}

AsetGambarFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default AsetGambarFormDialog;
